import React, { useEffect, useState, useRef } from "react"
import { graphql } from "gatsby"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import Layout from "../../components/Layout"
import { documentToHtmlString } from "@contentful/rich-text-html-renderer"
import RichText from "../../components/RichText";
import { Helmet } from "react-helmet"

import "./jobs.scss"
const emptyFilters = {
  location: [],
  department: [],
}

const scriptURL = "https://boards.greenhouse.io/embed/job_board/js?for=ionq"

export const pageQuery = graphql`
  {
    allGreenhouseJob {
      edges {
        node {
          title
          gh_Id
          location {
            name
          }
          departments {
            name
          }
        }
      }
    }
    allContentfulCareerPageTab(sort: { position: ASC }) {
      nodes {
        title
        content {
          raw
        }
      }
    }
  }
`

const CareerPageTab = ({ tabInfo, setActiveTab, active }) => {
  return (
    <div onClick={e => setActiveTab(tabInfo.title)} className="tab" className={`culture-tab ${active ? "-active" : ""}`}>
      { tabInfo.title }
    </div>
  )
}

const Jobs = ({ location, data }) => (
  <Layout
    className="Jobs"
    id="jobs"
    description="Make History. Build the future of computing. View open positions and apply online."
    title="Jobs at IonQ"
    location={location}
    darkMode={true}
  >
    <div className="jobs-content">
      <div className="jobs-hero">
        <h1 className="jobs-hero-headline">
          Make History.
          <br />
          Build the Future of Computing
          <span aria-hidden className="color-dot">
            .
          </span>
        </h1>
        <div className="jobs-hero-cta-container">
          <AnchorLink className="jobs-hero-cta" to="#open-positions">
            View Open Positions
          </AnchorLink>
        </div>
      </div>
      <div className="jobs-intro-section">
        <JobsBoard location={location} jobData={data} />
      </div>
    </div>
  </Layout>
)

const JobsBoard = ({ location, jobData }) => {
  const [flattenedJobsList, updateFlattenedJobsList] = useState(null)

  const [typeDownInput, updateTypedownInput] = useState("")
  const [departmentNames, updateDepartmentNames] = useState([])
  const [locationNames, updateLocationNames] = useState([])
  const [filtering, updateFiltering] = useState(false)

  const [filters, updateFilters] = useState(emptyFilters)
  const [jobsToDisplay, updateJobsToDisplay] = useState([])

  const ghSrc = useRef()
  useEffect(() => {
    if (window) {
      ghSrc.current = new URL(location.href).searchParams.get("gh_src")
    }
  })

  const [locationFiltersActive, updateLocationFiltersActive] = useState(false)
  const [teamsFiltersActive, updateTeamsFiltersActive] = useState(false)

  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    // clean up and populate fields
    const jobsList = jobData.allGreenhouseJob.edges
      .map(job => ({
        ...job.node,
        department: (job.node.departments[0] || { name: "" }).name.trim(), // assumes we don't list multi-department!
        location: job.node.location.name.trim(),
      }))
      .sort((a, b) => a.title > b.title)

    const namesOfDepartmentsWithJobs = jobsList
      .reduce((acc, curr) => [...acc, curr.department.trim()], [])
      .filter((item, index, arr) => arr.indexOf(item) === index) //uniques only!
      .filter(item => !!item) //no undefined!
      .sort()
    updateDepartmentNames(namesOfDepartmentsWithJobs)

    const namesOfLocationsWithJobs = jobsList
      .reduce((acc, curr) => [...acc, curr.location.trim()], [])
      .filter((item, index, arr) => arr.indexOf(item) === index) //uniques only!
      .sort()
    updateLocationNames(namesOfLocationsWithJobs)

    updateFlattenedJobsList(jobsList)

    setActiveTab(jobData.allContentfulCareerPageTab.nodes[0].title)
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterClick = e => {
    //FIXME: listening for meta attrs on the elements is a very un-reactive way of doing this but I'm in a hurry rn
    const category = e.target.attributes["meta-category"].value
    const clickedValue = e.target.attributes["meta-filter"].value
    if (filters[category].includes(clickedValue)) {
      updateFilters({
        ...filters,
        [category]: filters[category].filter(val => val !== clickedValue),
      })
    } else {
      updateFilters({
        ...filters,
        [category]: [...filters[category], clickedValue],
      })
    }
  }

  const clearFilters = e => {
    updateFilters(emptyFilters)
    updateTypedownInput("")
  }

  useEffect(() => {
    // filters
    let filtered = flattenedJobsList
    let isFiltering = true
    if (filters.location.length) {
      filtered = filtered.filter(job => filters.location.includes(job.location))
    }
    if (filters.department.length) {
      filtered = filtered.filter(job =>
        filters.department.includes(job.department)
      )
    }
    if (typeDownInput.trim()) {
      const regex = new RegExp(`${typeDownInput.trim()}`, "gi")
      filtered = filtered.filter(job =>
        `${job.location} ${job.department} ${job.title} `.match(regex)
      )
    }
    if (
      filters.location.length === 0 &&
      filters.department.length === 0 &&
      !typeDownInput
    ) {
      isFiltering = false
    }
    updateFiltering(isFiltering)
    updateJobsToDisplay(filtered)
  }, [flattenedJobsList, filters, typeDownInput])

  const handleChangeClientState = (_newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(
        ({ src }) => src === scriptURL
      )
      if (foundScript) {
        foundScript.addEventListener("load", () => {
          //loaded
          window.Grnhse.Iframe.load();
        })
      }
    }
  }

  return (
    <>
      <div id="career-page-tabs-container">
        <div className="video-container">
          <h1>
            IonQ is an Exciting Place to Work
          </h1>
          <p>
            Join the Quantum Revolution at IonQ! Meet some of the leaders behind our mission to tackle the world's toughest problems, and why they chose to work with the brilliant minds at IonQ.
          </p>
          <iframe
            src="https://www.youtube.com/embed/9xhuoUDXKbg?si=1eS4a518Rfjn1QwR"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className="tabs-container">
          {
            jobData.allContentfulCareerPageTab.nodes.map(tab => (
              <CareerPageTab
                tabInfo={tab}
                setActiveTab={setActiveTab}
                active={activeTab === tab.title}
                key={tab.title}
              />
            ))
          }
        </div>
        <div className="tab-contents">
          {
            activeTab && (
              <RichText
                richTextContent={jobData.allContentfulCareerPageTab.nodes.find(tab => tab.title === activeTab)?.content}
              />
            )
          }
        </div>
      </div>
      <div id="open-positions">
        <h2 className="jobs-board-headline">Open Positions</h2>
        <div className="jobs-board-headline">
          <div id="grnhse_app"></div>
        </div>
      </div>
      <Helmet onChangeClientState={handleChangeClientState}>
        {typeof window !== "undefined" &&
          typeof window.Grnhse === "undefined" && (
            <script async defer src={scriptURL} />
          )}
      </Helmet>
    </>
  )
}

const Job = ({ job, ghSrc }) => (
  <a
    className="job-item"
    href={`/careers/${job.gh_Id}${ghSrc ? `?gh_src=${ghSrc}` : ``}`}
  >
    <h4 className="job-title">{job.title}</h4>
    <div className="job-location">{job.location}</div>
    <div className="job-department">{job.department}</div>
  </a>
)

export default Jobs